import {
  addToast,
  Button,
  Checkbox,
  CircularSpinnerß,
  cx,
  ErrorToast,
  Label,
  SuccessToast,
  Toastß,
} from '@otter-ai/den-components';
import { DropboxIcon, GreenCheckIcon } from '@otter-ai/den-icons';
import { getDesktopAppUtmContent } from '@otter-ai/desktop';
import { PLAN_TYPE_TO_PLAN_NAME } from '@otter-ai/zone-purchase/src/lib/DO_NOT_USE';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { type ReactElement, type ReactNode, useMemo, useState } from 'react';
import { PATH_URL } from '@/constants/path-constants';
import { useUserFeatureToPlans } from '@/features/account-settings/shared/hooks/useUserFeatureToPlans';
import { FormattedText } from '@/shared/components/FormattedText';
import { useGetCurrentUserFeatures } from '@/shared/hooks/current-user/useGetCurrentUserFeatures';
import { usePromiseValue } from '@/shared/hooks/usePromiseValue';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { BuiltInApps } from '@/shared/types/apps';
import type { OtterPlanType } from '@/shared/types/plan';
import { isBasicWorkspaceMember } from '@/shared/utils/workspaceUtils';
import { descriptionFormatRules } from '../../shared/utils/text';
import { useDisconnectApp } from '../hooks/useDisconnectApp';
import { useDropboxExportSettings } from '../hooks/useDropboxExportSettings';
import { useDropboxOAuth } from '../hooks/useDropboxOAuth';
import { useSyncedAccounts } from '../hooks/useSyncedAccounts';
import { DROPBOX_TEST_IDS } from '../test-ids/dropbox';
import { trackDropbox } from '../tracking/dropbox-tracking';
import type { DropboxSyncedAccount } from '../type/apps';
import { AppRowLayout } from './AppRowLayout';
import { DisconnectedButton } from './DisconnectedButton';
export function DropboxAppRow() {
  const t = useTranslations('settings:integrations.apps.dropbox');
  const {
    accounts,
    isLoading,
    hasSyncedAccounts: isConnected,
  } = useSyncedAccounts({
    service: BuiltInApps.DROPBOX,
  });
  const { disconnectAppAsync } = useDisconnectApp();
  const { prefetchData, openDropboxAuthorizationPage } = useDropboxOAuth();
  const showUpgradeButton = useShowUpgradeButton();
  const canConnectDropbox = useCanConnectDropbox();
  prefetchData();

  const purchaseTeamPlanHref = useMemo(() => {
    const utmContent = getDesktopAppUtmContent();
    return `${PATH_URL.PURCHASE_TEAM_PLAN}?utm_content=${encodeURIComponent(
      utmContent,
    )}`;
  }, []);

  const handleDisconnect = async (account: DropboxSyncedAccount) => {
    trackDropbox.main.disconnectDropbox();
    try {
      await disconnectAppAsync({ service: BuiltInApps.DROPBOX, account });
      addToast(
        <SuccessToast
          description={t('disconnectSuccess', { accountName: account.account })}
        />,
      );
      return true;
    } catch {
      addToast(
        <ErrorToast
          description={t('disconnectError', { accountName: account.account })}
        />,
      );
      return false;
    }
  };

  return (
    <AppRowLayout
      appRowId="dropbox-app"
      Icon={DropboxIcon}
      title="Dropbox"
      description={<DropboxDescription />}
      buttons={
        <div className="flex flex-col gap-2">
          {!isConnected && canConnectDropbox && (
            <Button
              disabled={isLoading}
              onClick={() => {
                trackDropbox.main.connectDropbox();
                openDropboxAuthorizationPage();
              }}
              data-testid={DROPBOX_TEST_IDS.connectButton}
            >
              {t('connectButton')}
            </Button>
          )}
          {!isConnected && showUpgradeButton && (
            <Button
              asChild
              onClick={() => {
                trackDropbox.main.dropboxPlanUpgrade();
              }}
              data-testid={DROPBOX_TEST_IDS.connectButton}
            >
              <Link
                href={purchaseTeamPlanHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('upgradeButton')}
              </Link>
            </Button>
          )}
        </div>
      }
      linkedAccounts={accounts.map((account) => ({
        accountName: account.account,
        accountId: account.account,
        button: (
          <DisconnectedButton
            disconnectButtonText={t('disconnectButton')}
            disconnectAction={() => handleDisconnect(account)}
            tooltipCaption={t('disconnectTooltip', {
              accountName: account.account,
            })}
            data-testid={DROPBOX_TEST_IDS.disconnectButton}
          />
        ),
      }))}
      extendContent={
        accounts.length > 0 && (
          <DropboxExportSettings dropboxAccount={accounts[0]} />
        )
      }
    />
  );
}

function useIsDropboxSyncSupported() {
  const { data: userFeatures } = useGetCurrentUserFeatures();
  const isDropboxSyncSupported = useMemo(() => {
    if (!userFeatures) {
      return false;
    }
    return userFeatures.allow_dropbox_sync;
  }, [userFeatures]);

  return isDropboxSyncSupported;
}

function useCanConnectDropbox() {
  const { data: userFeatures } = useGetCurrentUserFeatures();
  return userFeatures?.allow_dropbox_sync ?? false;
}

function useShowUpgradeButton() {
  const { adminStatus, workspace } = useWorkspace();
  const isDropboxSyncSupported = useIsDropboxSyncSupported();
  return useMemo(() => {
    if (isBasicWorkspaceMember(adminStatus, workspace)) {
      // Only the admins of a basic workspace can upgrade to a paid plan
      return false;
    }
    return !isDropboxSyncSupported;
  }, [adminStatus, workspace, isDropboxSyncSupported]);
}

function DropboxDescription() {
  const t = useTranslations('settings:integrations.apps.dropbox');
  const isDropboxSyncSupported = useIsDropboxSyncSupported();
  const { hasSyncedAccounts } = useSyncedAccounts({
    service: BuiltInApps.DROPBOX,
  });

  const lowestSupportedPlan = useLowestTierPlanForDropbox();

  if (!hasSyncedAccounts) {
    return (
      <FormattedText>
        {t.rich('descriptionDisconnected', descriptionFormatRules())}
      </FormattedText>
    );
  }

  if (isDropboxSyncSupported) {
    return (
      <FormattedText>
        {t.rich('descriptionConnected', descriptionFormatRules())}
      </FormattedText>
    );
  }

  if (!lowestSupportedPlan) {
    return null;
  }

  const lowestSupportedPlanName =
    PLAN_TYPE_TO_PLAN_NAME[
      lowestSupportedPlan as keyof typeof PLAN_TYPE_TO_PLAN_NAME
    ];

  if (!lowestSupportedPlanName) {
    throw new MissingDropboxLowestPlanName(lowestSupportedPlan);
  }

  // Has a synced account, but their plan does not allow it
  return (
    <FormattedText>
      {t.rich('descriptionDisabledUpgradePlan', {
        ...descriptionFormatRules(),
        lowestSupportedPlan: lowestSupportedPlanName,
      })}
    </FormattedText>
  );
}

function useLowestTierPlanForDropbox(): OtterPlanType | null {
  const { getLowestTierSupportedPlanForFeature } = useUserFeatureToPlans();
  const lowestSupportedPlanPromise = useMemo(
    () => getLowestTierSupportedPlanForFeature('allow_dropbox_sync'),
    [getLowestTierSupportedPlanForFeature],
  );
  const lowestSupportedPlan = usePromiseValue(lowestSupportedPlanPromise);
  return lowestSupportedPlan;
}

function DropboxExportSettings({
  dropboxAccount,
}: {
  dropboxAccount: DropboxSyncedAccount;
}) {
  const t = useTranslations(
    'settings:integrations.apps.dropbox.exportSettings',
  );
  const {
    canEditExportSettings,
    isDropboxExportEnabled,
    openDropboxExportSettingsModal,
    updateDropboxExportEnabled,
  } = useDropboxExportSettings();

  // ----- Controlled state -----
  const [isExportChecked, setIsExportChecked] = useState(
    isDropboxExportEnabled,
  );
  const [isExportPending, setIsExportPending] = useState(false);

  const [showModifyButton, setShowModifyButton] = useState(
    isDropboxExportEnabled,
  );
  const [isModifyButtonDisabled, setIsModifyButtonDisabled] = useState(false);
  const [isModifyButtonPending, setIsModifyButtonPending] = useState(false);

  // ----- Event handlers -----
  const handleUpdateExportEnabled = async (enabled: boolean) => {
    // If the update fails, we need to revert the state to the previous value
    const previousEnabled = isExportChecked;

    // UI state:
    // - Export checkbox: show pending spinner
    // - Modify button: show and disable the button if the export is changed to enabled
    setIsExportPending(true);
    setIsExportChecked(enabled);
    setIsModifyButtonDisabled(true);
    setShowModifyButton(enabled);
    trackDropbox.main.dropboxToggleAutoExport(enabled);
    try {
      await updateDropboxExportEnabled({
        enabled,
        dropboxAccount,
      });
    } catch {
      // UI state: revert the checkbox and modify button visibility
      setIsExportChecked(previousEnabled);
      setShowModifyButton(previousEnabled);
      addToast(<ErrorToast description={t('savingError')} />);
    } finally {
      // UI state:
      // - Export checkbox: hide pending spinner
      // - Modify button: enable the button (button may not be visible)
      setIsExportPending(false);
      setIsModifyButtonDisabled(false);
    }
  };
  const handleModifyButtonClick = async () => {
    // UI state for the modify button:
    // - Disable the button
    // - Show pending spinner
    setIsModifyButtonDisabled(true);
    setIsModifyButtonPending(true);
    trackDropbox.main.openModifyExportSettings();
    try {
      const saved = await openDropboxExportSettingsModal();
      if (saved) {
        addToast(
          <Toastß description={t('savingSuccess')} icon={<GreenCheckIcon />} />,
        );
      }
    } catch {
      addToast(<ErrorToast description={t('savingError')} />);
    } finally {
      // UI state for the modify button:
      // - Enable the button
      // - Hide pending spinner
      setIsModifyButtonDisabled(false);
      setIsModifyButtonPending(false);
    }
  };

  if (!canEditExportSettings) {
    return null;
  }

  return (
    <DropboxExportSettingsLayout
      enableCheckbox={
        <Checkbox
          checked={isExportChecked}
          onCheckedChange={handleUpdateExportEnabled}
          data-testid={DROPBOX_TEST_IDS.exportSettingsButton}
        />
      }
      enableLabel={t('enableExport')}
      showEnableLoading={isExportPending}
      showModifyButton={showModifyButton}
      showModifyLoading={isModifyButtonPending}
      modifyButtonText={t('modifyExportSettings')}
      modifyButtonAction={handleModifyButtonClick}
      modifyButtonDisabled={isModifyButtonDisabled}
    />
  );
}

function DropboxExportSettingsLayout({
  enableCheckbox,
  enableLabel,
  showEnableLoading,
  showModifyButton,
  showModifyLoading,
  modifyButtonDisabled,
  modifyButtonText,
  modifyButtonAction,
}: {
  enableCheckbox: ReactElement;
  enableLabel: ReactNode;
  showEnableLoading: boolean;
  showModifyButton: boolean;
  showModifyLoading: boolean;
  modifyButtonDisabled: boolean;
  modifyButtonText: ReactNode;
  modifyButtonAction: () => void;
}) {
  const disableModifyButton = showModifyLoading || modifyButtonDisabled;
  return (
    <div
      className="flex flex-col gap-2 mt-4"
      data-testid={DROPBOX_TEST_IDS.exportSettings}
    >
      <div className="flex items-center gap-2">
        <Label control={enableCheckbox}>{enableLabel}</Label>
        <CircularSpinnerß
          size="xxsm"
          className={cx(!showEnableLoading && 'opacity-0')}
          data-testid="dropbox-enable-loading"
        />
      </div>
      {showModifyButton && (
        <div className="flex items-center gap-2">
          <button
            className={cx(
              'text-sm underline',
              !disableModifyButton && 'text-primary',
              disableModifyButton &&
                'text-button-primary-text-disabled cursor-wait',
            )}
            onClick={modifyButtonAction}
            disabled={disableModifyButton}
            data-testid={DROPBOX_TEST_IDS.exportSettingsButton}
          >
            {modifyButtonText}
          </button>
          <CircularSpinnerß
            size="xxsm"
            className={cx(!showModifyLoading && 'opacity-0')}
            data-testid="dropbox-modify-loading"
          />
        </div>
      )}
    </div>
  );
}

class MissingDropboxLowestPlanName extends Error {
  name = 'MissingDropboxLowestPlanName';
  constructor(lowestSupportedPlan: OtterPlanType) {
    super(`Missing the plan name for ${lowestSupportedPlan}`);
  }
}
